import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Stream from 'flarum/common/utils/Stream';
import { PORTS, SHIPPING, COUNTRIES, CATEGORIES, ELEMENTS, CROPS, POPULAR, FACILITY_TYPES, STATUSES, ECOWAS } from './data';
import { matches, nearestPorts, km } from './geo';
import MapController from './MapController';
import { planMethods } from './plans';
import { chainFor } from './valueChains';
const countryName = code => (COUNTRIES.find(c => c[0] === code) || [])[1] || code || '';
const LAYERS = [['resources', 'Resources'], ['facilities', 'Facilities'], ['roads', 'Roads'], ['rail', 'Rail'], ['shipping', 'Shipping'], ['ports', 'Ports'], ['rivers', 'Rivers & lakes'], ['population', 'Population']];
const TABS = [['overview', 'Overview'], ['waterways', 'Waterways'], ['trade', 'Trade connections'], ['sources', 'Sources & similar sites']];
// Resources and crops with a known value chain also get these two
const CHAIN_TABS = [['businesses', 'Derivative businesses'], ['products', 'Final products']];
const MEMBER_KINDS = ['resource', 'crop', 'facility'];
const INTENT = 'voucha-rm-suggest'; // remembers "Suggest a resource" across the sign-in page reload
const KINDS = [['resource', 'Resource site (mine, oil field…)'], ['crop', 'Crop area'], ['facility', 'Facility (refinery, power plant, factory…)'], ['port', 'Port'], ['rail', 'Rail line']];
const GATE_SECONDS = 30; // visitors who aren't logged in are asked to join after this much time on the Atlas
const reducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const api = () => app.forum.attribute('apiUrl') + '/resource-map/items';
const lineKm = g => Math.round((g || []).reduce((t, p, i) => i ? t + km({
  lat: g[i - 1][1],
  lng: g[i - 1][0]
}, {
  lat: p[1],
  lng: p[0]
}) : 0, 0));
const blank = function (kind) {
  if (kind === void 0) {
    kind = 'facility';
  }
  return {
    kind,
    type: kind === 'facility' ? 'refinery' : '',
    name: '',
    country: '',
    area: '',
    category: 'minerals',
    element: '',
    symbol: '',
    resource: '',
    crop: '',
    lat: null,
    lng: null,
    geometry: [],
    status: 'operational',
    online: '',
    note: '',
    sources: [{
      name: '',
      url: ''
    }, {
      name: '',
      url: ''
    }]
  };
};

/** /resource-map: natural resources, crops, facilities and trade connections across coastal West Africa */
export default class ResourceMapPage extends Page {
  oninit(vnode) {
    this.place = null; // { slug, type: 'state' | 'lga', name, state } : items in that state or LGA
    this.states = [];
    this.lgas = [];
    this.loadPlaces();
    super.oninit(vnode);
    // Opens on Nigeria: Nigerian sites, every layer on except Resources, "ECOWAS only" off
    this.f = {
      query: m.route.param('q') || '',
      category: '',
      crop: '',
      element: '',
      country: 'NG'
    };
    // ?embed=1: the map alone, for the app's Atlas screen (it has its own search, lists and pages). The app can
    // pass the filters in the address (category, crop, element, country; country=all for every country)
    this.embed = false;
    try {
      const qs = new URLSearchParams(window.location.search);
      this.embed = qs.get('embed') === '1';
      if (this.embed) {
        ['category', 'crop', 'element'].forEach(k => {
          if (qs.get(k)) this.f[k] = qs.get(k);
        });
        if (qs.has('country')) this.f.country = qs.get('country') === 'all' ? '' : qs.get('country');
      }
    } catch (e) {}
    this.layers = {
      resources: false,
      facilities: true,
      roads: true,
      rail: true,
      shipping: true,
      ports: true,
      rivers: true,
      population: true
    };
    this.ecowasOnly = false;
    this.southUp = false; // the map turned 180° (labels stay upright)
    this.toolsOpen = false; // phones: map options folded behind one button
    this.style = 'terrain';
    this.animate = !reducedMotion();
    this.items = [];
    this.canEdit = false;
    this.selected = null;
    this.tab = 'overview';
    this.water = null;
    this.edit = null; // { data, id, mode: 'edit' | 'suggest', error, saving } while adding, editing or suggesting
    this.thanks = false; // after a member's suggestion is sent
    this.pending = 0; // suggestions waiting (editors)
    this.review = null; // the review list (editors)
    this.error = null;
    this.ready = false;
    this.wanted = parseInt(m.route.param('item'), 10) || null; // /atlas/12-dangote-... opens item 12
    this.wantedPlan = String(m.route.param('plan') || '').split('-')[0] || null; // /atlas/plan/k7f3q9xw2m4a-... opens a plan
    this.plan = null;
    this.planEdit = null;
    this.plansList = null;
    app.setTitle('Voucha Atlas');
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    if (this.embed) this.startEmbed();else this.startGateTimer();
    const key = app.forum.attribute('vouchaResourceMapKey');
    if (!key) {
      this.error = 'The map needs a MapTiler key. An administrator can add it in Admin → Voucha Resource Map.';
      return m.redraw();
    }
    this.map = new MapController(vnode.dom.querySelector('.RMMap-canvas'), key, {
      onSelect: s => {
        this.select(s);
        if (this.embed) this.tellApp({
          item: s.path,
          id: s.dbId,
          name: s.name
        });
      },
      onError: msg => {
        this.error = msg;
        m.redraw();
      },
      layers: this.layers,
      ecowasOnly: this.ecowasOnly,
      cooperative: !this.embed // in the app the map has its own space: one finger pans
    });
    this.map.onFullscreen = () => this.toggleFullscreen();
    // Leaving full screen with Esc (or the browser's own controls) keeps everything in step
    this.onFsChange = () => {
      const native = document.fullscreenElement || document.webkitFullscreenElement;
      if (this.fullscreen && !native && !this.fsWindowed) this.setFullscreenState(false);
    };
    this.onFsKey = e => {
      if (e.key === 'Escape' && this.fullscreen && this.fsWindowed) this.toggleFullscreen();
    };
    document.addEventListener('fullscreenchange', this.onFsChange);
    document.addEventListener('webkitfullscreenchange', this.onFsChange);
    document.addEventListener('keydown', this.onFsKey);
    this.map.init(this.style).then(() => {
      this.ready = true;
      this.map.onRotate = south => {
        if (south !== this.southUp) {
          this.southUp = south;
          m.redraw();
        }
      };
      this.map.setAnimate(this.animate);
      if (this.area && this.place) this.map.showArea(this.area); // a place from a shared link, chosen before the map was ready
      this.loadItems();
      app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/resource-map/ecowas',
        errorHandler: () => {}
      }).then(d => this.map && this.map.setEcowas(d)).catch(() => {});
      app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/resource-map/population',
        errorHandler: () => {}
      }).then(d => this.map && this.map.setPopulation(d)).catch(() => {});
      m.redraw();
    }).catch(e => {
      this.error = 'The map could not load (' + e.message + '). Please refresh the page.';
      m.redraw();
    });
  }
  onremove(vnode) {
    super.onremove(vnode);
    clearInterval(this.gateTimer);
    document.documentElement.classList.remove('RM-gated', 'RM-embed');
    if (this.embed) delete window.VouchaAtlas;
    if (this.fullscreen) this.toggleFullscreen();
    document.removeEventListener('fullscreenchange', this.onFsChange);
    document.removeEventListener('webkitfullscreenchange', this.onFsChange);
    document.removeEventListener('keydown', this.onFsKey);
    if (this.map) this.map.destroy();
  }

  /**
   * Full screen for the whole map block (map, options, legend, panels), not just the canvas. Uses the
   * browser's full screen where it exists; otherwise (iPhone Safari) the block fills the window.
   */
  toggleFullscreen() {
    const el = this.element && this.element.querySelector('.RMMap');
    if (!el) return;
    if (!this.fullscreen) {
      const request = el.requestFullscreen || el.webkitRequestFullscreen;
      this.fsWindowed = !request;
      if (request) {
        Promise.resolve(request.call(el)).catch(() => {
          this.fsWindowed = true;
          this.setFullscreenState(true);
        });
      }
      this.setFullscreenState(true);
    } else {
      const native = document.fullscreenElement || document.webkitFullscreenElement;
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      if (native && exit) Promise.resolve(exit.call(document)).catch(() => {});
      this.setFullscreenState(false);
    }
  }
  setFullscreenState(on) {
    this.fullscreen = on;
    if (!on) this.fsWindowed = false;
    const el = this.element && this.element.querySelector('.RMMap');
    if (el) el.classList.toggle('is-fullscreen', on);
    document.documentElement.classList.toggle('RM-noScroll', on && !!this.fsWindowed);
    if (this.map) this.map.setFullscreen(on);
    m.redraw();
  }

  /**
   * The app's Atlas screen shows this page as its map (?embed=1): only the map, and the app drives it.
   * window.VouchaAtlas.set({ q, category, crop, element, country, place, item, plan }) changes what it shows
   * (country '' = every country; item = an item's id, to centre on it; plan = a plan's token; place = a state or
   * LGA slug, '' to clear). A tap on a site is sent to the app (tellApp), which opens its own page.
   */
  startEmbed() {
    document.documentElement.classList.add('RM-embed');
    window.VouchaAtlas = {
      set: o => {
        o = o || {};
        ['category', 'crop', 'element', 'country'].forEach(k => {
          if (k in o) this.f[k] = o[k] || '';
        });
        if (o.category || o.crop || o.element) this.showResources();
        if ('place' in o && (o.place || '') !== (this.place ? this.place.slug : '')) this.choosePlace(o.place || '');
        if ('q' in o) this.search(o.q || '');else this.refresh();
        if (o.plan) this.openPlan(o.plan);else if ('plan' in o && this.plan) this.closePlan();
        if (o.item) {
          const it = this.items.find(i => i.dbId === Number(o.item));
          if (it) {
            if (this.f.country && it.country && it.country !== this.f.country) {
              this.f.country = '';
              this.refresh(false);
            }
            this.select(it);
          }
        }
        m.redraw();
      }
    };
  }
  tellApp(msg) {
    try {
      if (window.ReactNativeWebView) window.ReactNativeWebView.postMessage(JSON.stringify({
        atlas: msg
      }));
    } catch (e) {}
  }

  /**
   * Visitors who aren't logged in: after GATE_SECONDS of time with the Atlas on screen (a hidden tab
   * doesn't count), the page blurs and a prompt asks them to join or log in to keep exploring.
   */
  startGateTimer() {
    if (app.session.user) return;
    this.gated = false;
    let seen = 0;
    this.gateTimer = setInterval(() => {
      if (app.session.user) return clearInterval(this.gateTimer);
      if (document.visibilityState !== 'visible') return;
      seen++;
      if (seen >= GATE_SECONDS) {
        clearInterval(this.gateTimer);
        if (this.fullscreen) this.toggleFullscreen(); // the prompt must be on screen
        this.gated = true;
        document.documentElement.classList.add('RM-gated'); // no scrolling the blurred page behind
        m.redraw();
      }
    }, 1000);
  }

  /** Flarum 2 loads the sign-up and log-in dialogs on demand; if that fails, use the header's own buttons */
  openAuth(which) {
    const header = () => {
      const b = document.querySelector('#header-secondary .item-' + which + ' button, #header-secondary .item-' + which + ' .Button');
      if (b) b.click();
    };
    try {
      const shown = which === 'signUp' ? app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/SignUpModal')) : app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'));
      if (shown && typeof shown.catch === 'function') shown.catch(header);
    } catch (e) {
      header();
    }
  }
  gateView() {
    return m("div", {
      className: "RMGate",
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": "RMGate-title"
    }, m("div", {
      className: "RMGate-card"
    }, m("span", {
      className: "RMGate-icon",
      "aria-hidden": "true"
    }, m("i", {
      className: "fas fa-globe-africa"
    })), m("h2", {
      id: "RMGate-title"
    }, "Keep exploring the Voucha Atlas"), m("p", null, "Join Voucha to explore every mine, farm belt, power plant and trade route, and see the businesses and products each resource can support."), m("div", {
      className: "RMGate-actions"
    }, m("button", {
      type: "button",
      className: "Button Button--primary",
      onclick: () => this.openAuth('signUp')
    }, "Sign up"), m("button", {
      type: "button",
      className: "Button",
      onclick: () => this.openAuth('logIn')
    }, "Log in"))));
  }

  /** Everything on the map, from the database */
  loadItems(selectId) {
    if (selectId === void 0) {
      selectId = null;
    }
    return app.request({
      method: 'GET',
      url: api(),
      errorHandler: () => {}
    }).then(r => {
      this.items = r.items || [];
      this.canEdit = !!r.canEdit;
      this.pending = r.pending || 0;
      // Back from signing in to suggest something: open the form
      try {
        if (app.session.user && window.sessionStorage.getItem(INTENT)) {
          window.sessionStorage.removeItem(INTENT);
          if (!this.edit) this.startEdit(null, this.canEdit ? 'edit' : 'suggest');
        }
      } catch (e) {}
      if (this.map) this.map.setItems(this.items);
      // A link with a search (?q=lithium): same rules as searching on the page
      if (this.f.query) {
        if (this.f.country && !this.results().length) this.f.country = '';
        if (this.results().some(x => x.kind !== 'facility')) this.showResources();
      }
      this.refresh(false);
      const target = selectId || this.wanted;
      this.wanted = null;
      if (this.wantedPlan) {
        this.openPlan(this.wantedPlan);
        this.wantedPlan = null;
      }
      // Back from signing in to create a plan
      try {
        if (app.session.user && window.sessionStorage.getItem('voucha-rm-plan')) {
          window.sessionStorage.removeItem('voucha-rm-plan');
          this.startPlan();
        }
      } catch (e) {}
      if (target) {
        const it = this.items.find(i => i.dbId === target);
        if (it) {
          if (this.f.country && it.country && it.country !== this.f.country) {
            this.f.country = '';
            this.refresh(false);
          }
          this.select(it);
        }
      }
      m.redraw();
    }).catch(() => {
      this.error = 'The map items could not be loaded. Please refresh the page.';
      m.redraw();
    });
  }
  ports() {
    const p = this.items.filter(i => i.kind === 'port');
    return p.length ? p : PORTS;
  }

  /** Searchable items (not ports or rail lines) matching the filters */
  results() {
    return this.items.filter(s => ['resource', 'crop', 'facility'].includes(s.kind) && (!this.ecowasOnly || !s.country || ECOWAS.includes(s.country)) && matches(s, this.f, countryName) && this.inPlace(s));
  }

  /** Is the item in the chosen state or LGA? (no place chosen = everything) */
  inPlace(s) {
    if (!this.place) return true;
    if (!s.place) return false; // outside Nigeria, or not placed
    return this.place.type === 'lga' ? s.place.lga === this.place.slug : s.place.state === this.place.slug;
  }

  /** The states to pick from, and the place in the address (?place=ng-kano), if any */
  loadPlaces() {
    const api = app.forum.attribute('apiUrl');
    app.request({
      method: 'GET',
      url: api + '/datahub/places?type=state',
      errorHandler: () => {}
    }).then(r => {
      this.states = (r && r.places || []).slice().sort((a, b) => a.name.localeCompare(b.name));
      let wanted = null;
      try {
        wanted = new URLSearchParams(window.location.search).get('place');
      } catch (e) {}
      if (wanted) this.choosePlace(wanted, true);
      m.redraw();
    }).catch(() => {});
  }

  /** Choose a state or LGA by its slug ('' clears) */
  choosePlace(slug, fromAddress) {
    if (fromAddress === void 0) {
      fromAddress = false;
    }
    const api = app.forum.attribute('apiUrl');
    if (!slug) {
      this.place = null;
      this.lgas = [];
      if (this.map) this.map.showArea(null);
      this.setAddress(null);
      this.refresh();
      return;
    }
    const state = this.states.find(p => p.slug === slug);
    if (state) {
      this.place = {
        slug,
        type: 'state',
        name: state.name,
        state: slug
      };
      this.lgas = [];
      app.request({
        method: 'GET',
        url: api + '/datahub/places?type=lga&parent=' + encodeURIComponent(slug),
        errorHandler: () => {}
      }).then(r => {
        this.lgas = (r && r.places || []).slice().sort((a, b) => a.name.localeCompare(b.name));
        m.redraw();
      }).catch(() => {});
    } else {
      // an LGA: its state comes from the LGA list, or (from a shared link) from the boundary's parent below
      const lga = this.lgas.find(p => p.slug === slug);
      this.place = {
        slug,
        type: 'lga',
        name: lga ? lga.name : '',
        state: lga ? lga.parent : null
      };
    }
    app.request({
      method: 'GET',
      url: api + '/datahub/places/' + encodeURIComponent(slug) + '/shape',
      errorHandler: () => {}
    }).then(area => {
      if (this.place && this.place.slug === slug) {
        if (!this.place.name) this.place.name = area.name;
        if (this.place.type === 'lga' && !this.place.state && area.parent) {
          // a shared link to an LGA: now we know its state, load the state's LGAs for the picker
          this.place.state = area.parent;
          app.request({
            method: 'GET',
            url: api + '/datahub/places?type=lga&parent=' + encodeURIComponent(area.parent),
            errorHandler: () => {}
          }).then(r => {
            this.lgas = (r && r.places || []).slice().sort((a, b) => a.name.localeCompare(b.name));
            m.redraw();
          }).catch(() => {});
        }
        if (this.map) this.map.showArea(area);
        this.area = area;
      }
      m.redraw();
    }).catch(() => {});
    // the state's published masterplans (an LGA shows its state's)
    this.plans = [];
    app.request({
      method: 'GET',
      url: api + '/datahub/masterplans?place=' + encodeURIComponent(slug),
      errorHandler: () => {}
    }).then(r => {
      if (this.place && this.place.slug === slug) {
        this.plans = r && r.plans || [];
        m.redraw();
      }
    }).catch(() => {});
    if (!fromAddress) this.setAddress(slug);
    this.refresh(false); // the area's outline decides the zoom
  }
  setAddress(slug) {
    try {
      const url = new URL(window.location.href);
      if (slug) url.searchParams.set('place', slug);else url.searchParams.delete('place');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    } catch (e) {}
  }
  refresh(fit) {
    if (fit === void 0) {
      fit = true;
    }
    if (!this.map || !this.ready) return;
    const r = this.results();
    this.map.setVisible(r.map(s => s.id));
    const all = this.items.filter(s => ['resource', 'crop', 'facility'].includes(s.kind) && (!this.ecowasOnly || !s.country || ECOWAS.includes(s.country))).length;
    if (fit && r.length && r.length < all) this.map.fit(r);
  }
  setFilter(key, value) {
    this.f[key] = value;
    // Asking for a kind of resource (a category other than facilities, a crop or an element) shows them
    if (value && (key === 'crop' || key === 'element' || key === 'category' && value !== 'facilities')) this.showResources();
    this.refresh();
  }

  /** The Resources layer starts off; searching for, filtering to or opening a resource turns it on */
  showResources() {
    if (!this.layers.resources) this.toggleLayer('resources');
  }
  search(q) {
    this.f.query = q;
    // Nothing in the chosen country (e.g. Lithium while on Nigeria): look everywhere instead
    if (q && this.f.country && !this.results().length) this.f.country = '';
    if (q && this.results().some(x => x.kind !== 'facility')) this.showResources();
    this.refresh();
    const r = this.results();
    if (q && r.length === 1) this.select(r[0]);
  }
  select(site) {
    if (this.edit || this.planEdit) return;
    if (site && (site.kind === 'resource' || site.kind === 'crop')) this.showResources();
    this.selected = site;
    if (site && site.path && !this.embed) {
      try {
        window.history.replaceState(window.history.state, '', app.route('voucha-resourcemap.item', {
          item: site.path
        }));
      } catch (e) {}
      app.setTitle(site.name + ' · Voucha Atlas');
    }
    this.tab = 'overview';
    this.water = null;
    if (this.map) {
      if (site.kind !== 'rail') this.map.select(site);
      this.map.nearestWater(site).then(w => {
        if (this.selected === site) {
          this.water = w;
          m.redraw();
        }
      });
    }
    m.redraw();
  }
  toggleLayer(key) {
    this.layers[key] = !this.layers[key];
    if (this.map) this.map.setLayer(key, this.layers[key]);
  }
  setStyle(name) {
    this.style = name;
    if (this.map) this.map.setStyle(name);
  }
  setEcowasOnly(on) {
    this.ecowasOnly = on;
    if (this.map) this.map.setEcowasOnly(on);
    if (on && this.f.country && !ECOWAS.includes(this.f.country)) this.f.country = '';
    this.refresh(false);
  }
  setAnimate(on) {
    this.animate = on;
    if (this.map) this.map.setAnimate(on);
  }

  // ---------- editing (admins and Data editors) ----------
  /** The button everyone sees: visitors sign in first; members suggest; editors add directly */
  suggestOrAdd() {
    this.selected = null;
    this.thanks = false;
    if (!app.session.user) {
      try {
        window.sessionStorage.setItem(INTENT, '1');
      } catch (e) {}
      // Flarum 2 loads the log-in modal on demand (as its own sidebar does). If that fails for
      // any reason, fall back to the header's own "Log In" button.
      const headerLogIn = () => {
        const b = document.querySelector('#header-secondary .item-logIn button, #header-secondary .item-logIn .Button');
        if (b) b.click();
      };
      try {
        const shown = app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'));
        if (shown && typeof shown.catch === 'function') shown.catch(headerLogIn);
      } catch (e) {
        headerLogIn();
      }
      return;
    }
    this.startEdit(null, this.canEdit ? 'edit' : 'suggest');
  }
  startEdit(item, mode) {
    if (item === void 0) {
      item = null;
    }
    if (mode === void 0) {
      mode = 'edit';
    }
    const data = item ? {
      ...blank(item.kind),
      ...item,
      online: item.online || '',
      sources: [...(item.sources || []), {
        name: '',
        url: ''
      }, {
        name: '',
        url: ''
      }].slice(0, Math.max(2, (item.sources || []).length + 1))
    } : blank();
    this.edit = {
      data,
      id: item ? item.dbId : null,
      mode,
      error: null,
      saving: false
    };
    this.review = null;
    this.pick();
  }

  /** Start placing: a point, or a line for rail */
  pick() {
    if (!this.map || !this.edit) return;
    const d = this.edit.data;
    if (d.kind === 'rail') this.map.startPick('line', pts => {
      d.geometry = pts;
      m.redraw();
    }, d.geometry);else this.map.startPick('point', p => {
      d.lat = p.lat;
      d.lng = p.lng;
      m.redraw();
    }, d.lat != null ? {
      lat: d.lat,
      lng: d.lng
    } : null);
  }
  cancelEdit() {
    this.edit = null;
    if (this.map) this.map.stopPick();
  }
  save() {
    const e = this.edit;
    const d = {
      ...e.data,
      sources: e.data.sources.filter(s => s.url || s.name)
    };
    if (d.kind === 'crop') d.resource = d.crop;
    e.saving = true;
    e.error = null;
    const suggesting = e.mode === 'suggest';
    const url = suggesting ? app.forum.attribute('apiUrl') + '/resource-map/suggestions' : api() + (e.id ? '/' + e.id : '');
    app.request({
      method: suggesting || !e.id ? 'POST' : 'PATCH',
      url,
      body: d,
      errorHandler: () => {}
    }).then(r => {
      this.cancelEdit();
      this.selected = null;
      if (suggesting) {
        this.thanks = true;
        m.redraw();
        return null;
      }
      return this.loadItems(r.item.dbId);
    }).catch(err => {
      e.saving = false;
      e.error = err && err.response && err.response.errors && err.response.errors[0] && err.response.errors[0].detail || 'Saving failed. Please try again.';
      m.redraw();
    });
  }
  remove(item) {
    if (!window.confirm('Remove "' + item.name + '" from the map?')) return;
    app.request({
      method: 'DELETE',
      url: api() + '/' + item.dbId,
      errorHandler: () => {}
    }).then(() => {
      this.selected = null;
      if (this.map) this.map.select(null);
      return this.loadItems();
    });
  }

  /** The phone's share sheet (WhatsApp and the rest), or copy the link */
  share(s) {
    const url = window.location.origin + app.route('voucha-resourcemap.item', {
      item: s.path
    });
    const data = {
      title: s.name + ' · Voucha Atlas',
      text: s.name + (s.resource ? ' · ' + s.resource : ''),
      url
    };
    if (navigator.share) return navigator.share(data).catch(() => {});
    (navigator.clipboard ? navigator.clipboard.writeText(url) : Promise.reject()).then(() => {
      this.copied = s.dbId;
      m.redraw();
      setTimeout(() => {
        this.copied = null;
        m.redraw();
      }, 2500);
    }).catch(() => window.prompt('Copy this link:', url));
  }

  /**
   * Go to the item's discussion; if it has none, the member starts it in their own composer:
   * title and text pre-filled, the default tag (Finance & Business) selected, other tags up to them.
   * The text carries the item's Atlas link, which connects the new thread to the item.
   */
  discuss(s) {
    if (s.discussionId) return m.route.set('/d/' + s.discussionId);
    if (!app.session.user) {
      try {
        const shown = app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'));
        if (shown && typeof shown.catch === 'function') shown.catch(() => {});
      } catch (e) {}
      return;
    }
    this.starting = s.dbId;
    app.request({
      method: 'GET',
      url: api() + '/' + s.dbId + '/draft',
      errorHandler: () => {}
    }).then(draft => {
      const wanted = String(draft.tag || '').toLowerCase();
      const tag = (app.store.all('tags') || []).find(t => String(t.name() || '').toLowerCase() === wanted || String(t.slug() || '').toLowerCase() === wanted);
      // Flarum 2's load() returns the composer's component class (or nothing if the member has
      // unsaved text in an open composer and chose to keep it); the fields live on app.composer.
      return app.composer.load(() => flarum.reg.asyncModuleImport('flarum/forum/components/DiscussionComposer'), {
        user: app.session.user,
        originalContent: draft.content
      }).then(loaded => {
        if (!loaded) return; // they kept their unsaved draft
        app.composer.fields.title = Stream(draft.title);
        app.composer.fields.tags = tag ? [tag] : [];
        app.composer.show();
      });
    }).catch(err => {
      console.error('[Voucha Atlas] starting the discussion failed:', err); // never hide the real reason
      window.alert(err && err.response && err.response.errors && err.response.errors[0] && err.response.errors[0].detail || 'The discussion could not be started (' + (err && err.message || 'unknown error') + '). Please try again.');
    }).then(() => {
      this.starting = null;
      m.redraw();
    });
  }
  openReview() {
    this.selected = null;
    this.thanks = false;
    this.review = 'loading';
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/resource-map/suggestions',
      errorHandler: () => {}
    }).then(r => {
      this.review = r.suggestions || [];
      m.redraw();
    }).catch(() => {
      this.review = null;
      m.redraw();
    });
  }
  decide(s, approve) {
    if (!approve && !window.confirm('Reject "' + s.name + '"? It will be removed.')) return;
    const url = app.forum.attribute('apiUrl') + '/resource-map/suggestions/' + s.dbId + (approve ? '/approve' : '');
    app.request({
      method: approve ? 'POST' : 'DELETE',
      url,
      errorHandler: () => {}
    }).then(() => {
      this.review = this.review.filter(x => x !== s);
      this.loadItems();
    });
  }
  view() {
    const r = this.results();
    const total = this.items.filter(s => ['resource', 'crop', 'facility'].includes(s.kind)).length;
    const select = (key, icon, label, options) => m("label", {
      className: "RMSelect"
    }, m("i", {
      className: icon,
      "aria-hidden": "true"
    }), m("select", {
      value: this.f[key],
      onchange: e => this.setFilter(key, e.target.value),
      "aria-label": label
    }, m("option", {
      value: ""
    }, label), options.map(_ref => {
      let [v, t] = _ref;
      return m("option", {
        value: v
      }, t);
    })));
    if (this.embed) return m("div", {
      className: "RMPage RMPage--embed"
    }, this.mapView(r, total));
    return m("div", {
      className: 'RMPage' + (this.gated ? ' is-gated' : '')
    }, this.gated ? this.gateView() : null, m("section", {
      className: "RMHero"
    }, m("div", {
      className: "container"
    }, m("div", {
      className: "RMHero-eyebrow"
    }, "Voucha Atlas"), m("h1", null, "Resources, industry & trade"), m("p", null, "Mines, crops, refineries, power plants, factories, ports and rail across the ECOWAS member states."), m("form", {
      className: "RMSearch",
      onsubmit: e => {
        e.preventDefault();
        this.search(this.f.query);
      }
    }, m("i", {
      className: "fas fa-search",
      "aria-hidden": "true"
    }), m("input", {
      type: "search",
      placeholder: "Search resource, crop, facility, element or location\u2026",
      value: this.f.query,
      oninput: e => {
        this.f.query = e.target.value;
        this.refresh(false);
      },
      "aria-label": "Search the map"
    }), m("button", {
      type: "submit",
      className: "Button Button--primary"
    }, "Search")), m("div", {
      className: "RMChips"
    }, m("span", null, "Popular searches:"), POPULAR.map(p => m("button", {
      type: "button",
      className: 'RMChip' + (this.f.query === p ? ' is-active' : ''),
      onclick: () => this.search(p)
    }, p))))), m("div", {
      className: "container"
    }, m("div", {
      className: "RMBar"
    }, m("strong", null, "Filter resources:"), select('category', 'fas fa-layer-group', 'Category: All resources', [...CATEGORIES, ['facilities', 'Facilities']]), select('crop', 'fas fa-leaf', 'Crops: All crops', CROPS.map(c => [c, c])), select('element', 'fas fa-atom', 'Chemical elements: All elements', Object.entries(ELEMENTS).map(_ref2 => {
      let [s, n] = _ref2;
      return [s, s + ' · ' + n];
    })), select('country', 'fas fa-globe-africa', this.ecowasOnly ? 'All ECOWAS countries' : 'All countries', this.ecowasOnly ? COUNTRIES.filter(_ref3 => {
      let [c] = _ref3;
      return ECOWAS.includes(c);
    }) : COUNTRIES), this.states.length ? m("label", {
      className: "RMSelect"
    }, m("i", {
      className: "fas fa-map-marker-alt",
      "aria-hidden": "true"
    }), m("select", {
      value: this.place ? this.place.state : '',
      onchange: e => this.choosePlace(e.target.value),
      "aria-label": "State"
    }, m("option", {
      value: ""
    }, "Nigeria: all states"), this.states.map(p => m("option", {
      value: p.slug
    }, p.name)))) : null, this.place && this.lgas.length ? m("label", {
      className: "RMSelect"
    }, m("i", {
      className: "fas fa-map-pin",
      "aria-hidden": "true"
    }), m("select", {
      value: this.place.type === 'lga' ? this.place.slug : '',
      onchange: e => this.choosePlace(e.target.value || this.place.state),
      "aria-label": "LGA"
    }, m("option", {
      value: ""
    }, "All LGAs"), this.lgas.map(p => m("option", {
      value: p.slug
    }, p.name)))) : null), this.place ? m("div", {
      className: "RMPlaceNote"
    }, m("i", {
      className: "fas fa-map-marker-alt",
      "aria-hidden": "true"
    }), " ", m("strong", null, this.results().length), " site", this.results().length === 1 ? '' : 's', " in ", this.place.name || 'this area', this.place.type === 'lga' && this.place.state ? ' (' + ((this.states.find(p => p.slug === this.place.state) || {}).name || '') + ')' : '', m("button", {
      type: "button",
      className: "Button Button--link",
      onclick: () => this.choosePlace('')
    }, "Clear"), this.plans && this.plans.length ? m("span", {
      className: "RMPlaceNote-plans"
    }, m("i", {
      className: "fas fa-drafting-compass",
      "aria-hidden": "true"
    }), " Masterplans:", ' ', this.plans.map((p, i) => [i ? ', ' : '', m("a", {
      href: p.url,
      target: "_blank",
      rel: "noopener"
    }, p.title, p.year ? ' (' + p.year + ')' : '')])) : null) : null, m("div", {
      className: "RMBar"
    }, m("strong", null, "Map layers:"), m("button", {
      type: "button",
      className: 'RMLayer RMLayer--ecowas' + (this.ecowasOnly ? ' is-on' : ''),
      "aria-pressed": this.ecowasOnly ? 'true' : 'false',
      onclick: () => this.setEcowasOnly(!this.ecowasOnly),
      title: "Show only the 12 ECOWAS members"
    }, "ECOWAS only"), LAYERS.map(_ref4 => {
      let [key, label] = _ref4;
      const isLine = ['roads', 'rail', 'shipping', 'rivers'].includes(key);
      const off = isLine && this.style === 'land';
      return m("button", {
        type: "button",
        className: 'RMLayer' + (this.layers[key] && !off ? ' is-on' : '') + (off ? ' is-disabled' : ''),
        disabled: off,
        "aria-pressed": this.layers[key] && !off ? 'true' : 'false',
        title: off ? 'Lines are hidden in Land only' : '',
        onclick: () => this.toggleLayer(key)
      }, label);
    }), !this.edit ? m("span", {
      className: "RMAdd"
    }, m("button", {
      type: "button",
      className: "Button",
      onclick: () => this.openPlans()
    }, m("i", {
      className: "fas fa-route"
    }), " Plans"), this.canEdit && this.pending ? m("button", {
      type: "button",
      className: "Button",
      onclick: () => this.openReview()
    }, m("i", {
      className: "fas fa-inbox"
    }), " Review suggestions (", this.pending, ")") : null, m("button", {
      type: "button",
      className: "Button Button--primary",
      onclick: () => this.suggestOrAdd()
    }, m("i", {
      className: "fas fa-plus"
    }), " ", this.canEdit ? 'Add to map' : 'Suggest a resource')) : null), this.mapView(r, total), this.planEdit ? this.planEditor() : this.plansList ? this.plansPanel() : this.plan ? this.planView() : this.edit ? this.editor() : this.review ? this.reviewPanel() : this.thanks ? m("div", {
      className: "RMPanel RMThanks",
      role: "status"
    }, m("i", {
      className: "fas fa-check-circle",
      "aria-hidden": "true"
    }), " ", m("strong", null, "Thank you!"), " Your suggestion has been sent. An editor will check it, and it will appear on the map once approved.") : this.selected ? this.panel(this.selected) : m("div", {
      className: "RMPanel is-empty"
    }, m("i", {
      className: "fas fa-hand-pointer",
      "aria-hidden": "true"
    }), " Select a resource, crop, facility or rail line on the map to see its details.")));
  }

  /** The map, its options, legend and notes (on the page, or alone in the app) */
  mapView(r, total) {
    return m("div", {
      className: 'RMMap' + (this.edit ? ' is-editing' : '')
    }, m("div", {
      className: "RMMap-canvas"
    }), this.error ? m("div", {
      className: "RMMap-error",
      role: "alert"
    }, m("i", {
      className: "fas fa-map",
      "aria-hidden": "true"
    }), " ", this.error) : null, !this.ready && !this.error ? m("div", {
      className: "RMMap-loading"
    }, "Loading the map\u2026") : null, this.planEdit && (this.planEdit.drawing || this.planEdit.noting) ? m("div", {
      className: "RMMap-hint",
      role: "status"
    }, m("i", {
      className: "fas fa-mouse-pointer"
    }), " ", this.planEdit.drawing ? 'Click along the route, then Finish line.' : 'Click the map where the note goes.') : null, this.edit ? m("div", {
      className: "RMMap-hint",
      role: "status"
    }, m("i", {
      className: "fas fa-mouse-pointer"
    }), " ", this.edit.data.kind === 'rail' ? 'Click along the route to draw the line (' + this.edit.data.geometry.length + ' points).' : this.edit.data.lat != null ? 'Placed. Click again to move it.' : 'Click the map to place it.') : null, m("button", {
      type: "button",
      className: 'RMToolsToggle' + (this.toolsOpen ? ' is-open' : ''),
      "aria-expanded": this.toolsOpen ? 'true' : 'false',
      onclick: () => this.toolsOpen = !this.toolsOpen
    }, m("i", {
      className: 'fas ' + (this.toolsOpen ? 'fa-times' : 'fa-sliders-h'),
      "aria-hidden": "true"
    }), " ", this.toolsOpen ? 'Close' : 'Map options'), m("div", {
      className: 'RMMap-tools' + (this.toolsOpen ? ' is-open' : '')
    }, m("div", {
      className: "RMStyles",
      role: "group",
      "aria-label": "Map style"
    }, [['map', 'Map'], ['satellite', 'Satellite'], ['terrain', 'Terrain'], ['land', 'Land only']].map(_ref5 => {
      let [k, t] = _ref5;
      return m("button", {
        type: "button",
        className: this.style === k ? 'is-on' : '',
        onclick: () => this.setStyle(k)
      }, t);
    })), m("button", {
      type: "button",
      role: "switch",
      "aria-checked": this.animate ? 'true' : 'false',
      className: 'RMAnimate' + (this.animate ? ' is-on' : ''),
      onclick: () => this.setAnimate(!this.animate)
    }, m("span", {
      className: "RMAnimate-switch"
    }, m("span", null)), " Animate rivers"), m("button", {
      type: "button",
      role: "switch",
      "aria-checked": this.southUp ? 'true' : 'false',
      className: 'RMSouth' + (this.southUp ? ' is-on' : ''),
      title: "Turn the map so south is at the top",
      onclick: () => {
        this.southUp = !this.southUp;
        this.map && this.map.setSouthUp(this.southUp);
      }
    }, m("i", {
      className: "fas fa-sync-alt",
      "aria-hidden": "true"
    }), " South up"), m("button", {
      type: "button",
      className: "RMPause",
      "aria-label": this.animate ? 'Pause' : 'Play',
      onclick: () => this.setAnimate(!this.animate)
    }, m("i", {
      className: 'fas ' + (this.animate ? 'fa-pause' : 'fa-play'),
      "aria-hidden": "true"
    }))), m("div", {
      className: 'RMLegend' + (this.toolsOpen ? ' is-open' : '')
    }, m("span", null, m("b", {
      className: "RMDot is-resource"
    }, "Au"), " Resources"), m("span", null, m("b", {
      className: "RMDot is-crop"
    }, m("i", {
      className: "fas fa-leaf"
    })), " Crops"), m("span", null, m("b", {
      className: "RMDot is-facility"
    }, m("i", {
      className: "fas fa-industry"
    })), " Facilities"), m("span", null, m("i", {
      className: "RMLine is-road"
    }), " Roads"), m("span", null, m("i", {
      className: "RMLine is-rail"
    }), " Rail"), m("span", null, m("i", {
      className: "RMLine is-railproj"
    }), " Rail (built / under way / planned)"), m("span", null, m("i", {
      className: "RMLine is-ship"
    }), " Shipping"), m("span", null, m("b", {
      className: "RMDot is-port"
    }, m("i", {
      className: "fas fa-anchor"
    })), " Ports"), m("span", null, m("i", {
      className: "RMLine is-river"
    }), " Rivers & lakes"), m("span", null, m("b", {
      className: "RMPopKey"
    }, "2.3M"), " Population")), m("div", {
      className: "RMMap-note"
    }, m("i", {
      className: "fas fa-info-circle",
      "aria-hidden": "true"
    }), " ", r.length, " of ", total, " sites", this.style === 'land' ? ' · lines hidden (Land only)' : '', " \xB7 locations approximate \xB7 shipping lanes illustrative \xB7 borders: Natural Earth", this.place ? ' · state and LGA boundaries: geoBoundaries (CC BY 4.0)' : ''));
  }

  /** Discussions about this item (its main one, and any that link to it), newest activity first */
  discussionsPanel(s) {
    this.itemTalk = this.itemTalk || {};
    const key = s.dbId;
    if (key && this.itemTalk[key] === undefined) {
      this.itemTalk[key] = null;
      app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/resource-map/items/' + key + '/discussions',
        errorHandler: () => {}
      }).then(r => {
        this.itemTalk[key] = r && r.discussions || [];
        m.redraw();
      }).catch(() => {
        this.itemTalk[key] = [];
        m.redraw();
      });
    }
    const list = key ? this.itemTalk[key] : [];
    const ago = iso => {
      const d = (Date.now() - Date.parse(iso)) / 1000;
      return d < 3600 ? Math.max(1, Math.round(d / 60)) + 'm ago' : d < 86400 ? Math.round(d / 3600) + 'h ago' : d < 86400 * 30 ? Math.round(d / 86400) + 'd ago' : new Date(iso).toLocaleDateString();
    };
    return m("aside", {
      className: "RMTalk",
      "aria-label": "Discussions"
    }, m("h4", null, m("i", {
      className: "fas fa-comments",
      "aria-hidden": "true"
    }), " Discussions"), list === null ? m("p", {
      className: "RMMuted"
    }, "Loading\u2026") : list.length ? m("ul", null, list.map(d => {
      const url = app.route('discussion', {
        id: d.id + (d.slug ? '-' + d.slug : '')
      });
      return m("li", null, m("a", {
        href: url,
        onclick: e => {
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            m.route.set(url);
          }
        }
      }, d.title), m("span", {
        className: "RMMuted"
      }, d.replies, " repl", d.replies === 1 ? 'y' : 'ies', d.lastPostedAt ? ' · ' + ago(d.lastPostedAt) : ''));
    })) : m("div", null, m("p", {
      className: "RMMuted"
    }, "No discussions yet. Start one to ask or share what you know about ", s.name, "."), s.dbId ? m("button", {
      type: "button",
      className: "Button Button--primary",
      onclick: () => this.discuss(s)
    }, m("i", {
      className: "fas fa-comments",
      "aria-hidden": "true"
    }), " Start the discussion") : null));
  }
  panel(s) {
    const ports = s.lat != null ? nearestPorts(s, this.ports(), 3) : [];
    const same = this.items.filter(x => x !== s && x.kind === s.kind && (s.kind === 'facility' ? x.type === s.type : s.kind === 'rail' ? true : x.resource === s.resource));
    const lanes = p => SHIPPING.filter(_ref6 => {
      let [a, b] = _ref6;
      return a === p.id || b === p.id;
    }).map(_ref7 => {
      let [a, b] = _ref7;
      return (this.ports().find(x => x.id === (a === p.id ? b : a)) || {}).name;
    }).filter(Boolean);
    const what = s.kind === 'facility' ? (FACILITY_TYPES[s.type] || FACILITY_TYPES.other).label : s.kind === 'rail' ? 'Rail line' : s.kind === 'port' ? 'Port' : s.kind === 'crop' ? 'Crop' : 'Resource';
    const card = (icon, label, value, action) => m("div", {
      className: "RMCard"
    }, m("span", {
      className: "RMCard-icon"
    }, m("i", {
      className: icon,
      "aria-hidden": "true"
    })), m("div", null, m("small", null, label), action ? m("button", {
      type: "button",
      className: "RMCard-link",
      onclick: action
    }, value, " \u2192") : m("strong", null, value)));
    const status = STATUSES[s.status] || s.status;
    const chain = chainFor(s);
    if (!chain && (this.tab === 'businesses' || this.tab === 'products')) this.tab = 'overview';
    return m("section", {
      className: "RMPanel",
      "aria-label": s.name
    }, m("div", {
      className: "RMPanel-head"
    }, m("div", null, m("h2", null, s.name), m("p", {
      className: "RMMuted"
    }, [s.area, countryName(s.country)].filter(Boolean).join(', '), s.resource ? ' · ' + s.resource : '')), m("div", {
      className: "RMPanel-actions"
    }, s.kind !== 'port' ? m("button", {
      type: "button",
      className: "Button Button--primary RMDiscuss",
      disabled: this.starting === s.dbId,
      onclick: () => this.discuss(s)
    }, m("i", {
      className: "fas fa-comments"
    }), " ", this.starting === s.dbId ? 'Starting…' : s.discussionId ? 'Discuss' + (s.replies ? ' (' + s.replies + (s.replies === 1 ? ' reply)' : ' replies)') : '') : 'Start the discussion') : null, m("button", {
      type: "button",
      className: "Button",
      onclick: () => this.share(s)
    }, m("i", {
      className: "fas fa-share-alt"
    }), " ", this.copied === s.dbId ? 'Link copied' : 'Share'), this.canEdit ? m("button", {
      type: "button",
      className: "Button",
      onclick: () => this.startEdit(s)
    }, m("i", {
      className: "fas fa-pen"
    }), " Edit") : null, this.canEdit ? m("button", {
      type: "button",
      className: "Button RMDanger",
      onclick: () => this.remove(s)
    }, m("i", {
      className: "fas fa-trash"
    }), " Delete") : null, m("button", {
      type: "button",
      className: "Button",
      onclick: () => this.tab = 'sources'
    }, "Sources"))), m("div", {
      className: "RMCards"
    }, card(s.kind === 'facility' ? (FACILITY_TYPES[s.type] || FACILITY_TYPES.other).icon : s.kind === 'rail' ? 'fas fa-train' : s.kind === 'crop' ? 'fas fa-leaf' : s.kind === 'port' ? 'fas fa-anchor' : 'fas fa-gem', what, s.kind === 'rail' ? lineKm(s.geometry) + ' km' : (s.resource || what) + (s.element ? ' (' + s.element + ')' : '')), card('fas fa-flag-checkered', 'Status', status + (s.online ? ' · since ' + new Date(s.online + 'T00:00:00Z').toLocaleDateString('en-GB', {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC'
    }) : '')), ports.length && s.kind !== 'port' ? card('fas fa-anchor', 'Nearest port', ports[0].name + ' · ' + ports[0].km + ' km', () => this.tab = 'trade') : null), m("div", {
      className: "RMTabs",
      role: "tablist"
    }, (chain ? TABS.concat(CHAIN_TABS) : TABS).map(_ref8 => {
      let [k, t] = _ref8;
      return m("button", {
        type: "button",
        role: "tab",
        "aria-selected": this.tab === k ? 'true' : 'false',
        className: this.tab === k ? 'is-on' : '',
        onclick: () => this.tab = k
      }, t);
    })), m("div", {
      className: "RMTab"
    }, this.tab === 'overview' ? m("div", {
      className: "RMOverview"
    }, m("div", {
      className: "RMOverview-main"
    }, s.note ? m("p", null, s.note) : null, m("dl", {
      className: "RMFacts"
    }, m("dt", null, "Type"), m("dd", null, what), s.country ? [m("dt", null, "Country"), m("dd", null, countryName(s.country))] : null, s.area ? [m("dt", null, "Area"), m("dd", null, s.area)] : null, s.resource && s.kind !== 'port' ? [m("dt", null, s.kind === 'facility' ? 'Produces' : 'Resource'), m("dd", null, s.resource, s.element ? ' · element ' + s.element + ' (' + (ELEMENTS[s.element] || '') + ')' : '')] : null, m("dt", null, "Status"), m("dd", null, status, s.online ? ', since ' + s.online : ''), s.kind === 'rail' ? [m("dt", null, "Length"), m("dd", null, "about ", lineKm(s.geometry), " km (as drawn)")] : s.lat != null ? [m("dt", null, "Location"), m("dd", null, "about ", Math.abs(s.lat).toFixed(1), "\xB0", s.lat >= 0 ? 'N' : 'S', ", ", Math.abs(s.lng).toFixed(1), "\xB0", s.lng >= 0 ? 'E' : 'W', " (approximate)")] : null, s.origin === 'member' ? [m("dt", null, "Added by"), m("dd", null, "the community", s.by ? ' · ' + s.by : '')] : null)), this.discussionsPanel(s)) : null, this.tab === 'waterways' ? m("div", null, this.water === null ? m("p", {
      className: "RMMuted"
    }, "Looking up nearby rivers and lakes\u2026") : this.water.length ? m("ul", {
      className: "RMList"
    }, this.water.map(w => m("li", null, m("i", {
      className: 'fas ' + (w.kind === 'lake' ? 'fa-water' : 'fa-stream'),
      "aria-hidden": "true"
    }), " ", m("strong", null, w.name), " ", m("span", {
      className: "RMMuted"
    }, w.kind, " \xB7 about ", w.km, " km away")))) : m("p", {
      className: "RMMuted"
    }, "No named river or lake was found near here in the map data at this zoom level. Zoom in and select it again."), m("p", {
      className: "RMMuted RMSmall"
    }, "From OpenStreetMap data via MapTiler. Turn on \"Animate rivers\" to see the direction each river flows.")) : null, this.tab === 'trade' ? ports.length ? m("div", null, m("ul", {
      className: "RMList"
    }, ports.map(p => m("li", null, m("i", {
      className: "fas fa-anchor",
      "aria-hidden": "true"
    }), " ", m("strong", null, p.name), " ", m("span", {
      className: "RMMuted"
    }, countryName(p.country), " \xB7 about ", p.km, " km in a straight line"), lanes(p).length ? m("div", {
      className: "RMMuted RMSmall"
    }, "Coastal shipping to ", lanes(p).join(' and ')) : null))), m("p", {
      className: "RMMuted RMSmall"
    }, "Distances are straight-line; road and rail distances are longer. Shipping lanes on the map are illustrative.")) : m("p", {
      className: "RMMuted"
    }, "No location to measure from.") : null, this.tab === 'sources' ? m("div", {
      className: "RMSourcesTab"
    }, m("h4", null, "Sources"), m("ul", {
      className: "RMList"
    }, (s.sources || []).map(src => m("li", null, m("a", {
      href: src.url,
      target: "_blank",
      rel: "noopener"
    }, src.name || src.url, " ", m("i", {
      className: "fas fa-external-link-alt"
    })))), !(s.sources || []).length ? m("li", {
      className: "RMMuted"
    }, "No source added yet.") : null, m("li", {
      className: "RMMuted"
    }, "Map data \xA9 MapTiler \xA9 OpenStreetMap contributors (rivers, lakes, roads and rail)")), m("h4", null, "Similar sites"), same.length ? m("ul", {
      className: "RMList"
    }, same.map(x => m("li", null, m("button", {
      type: "button",
      className: "RMLinkish",
      onclick: () => this.select(x)
    }, x.name), " ", m("span", {
      className: "RMMuted"
    }, [x.area, countryName(x.country)].filter(Boolean).join(', '), " \xB7 ", STATUSES[x.status] || x.status)))) : m("p", {
      className: "RMMuted"
    }, "Nothing similar on the map yet.")) : null, this.tab === 'businesses' && chain ? m("div", {
      className: "RMChain"
    }, m("p", {
      className: "RMMuted RMSmall"
    }, "Businesses that can be set up along the ", chain.label.toLowerCase(), " supply chain, from the ", s.kind === 'crop' ? 'farm' : 'ground', " to the market. A general guide, not specific to this site."), chain.businesses.map(_ref9 => {
      let [stage, list] = _ref9;
      return m("div", {
        className: "RMChain-stage"
      }, m("h4", null, stage), m("ul", {
        className: "RMList"
      }, list.map(_ref0 => {
        let [name, what] = _ref0;
        return m("li", null, m("strong", null, name), " ", m("span", {
          className: "RMMuted"
        }, what));
      })));
    })) : null, this.tab === 'products' && chain ? m("div", {
      className: "RMChain"
    }, m("p", {
      className: "RMMuted RMSmall"
    }, "Everyday products made from ", chain.label.toLowerCase(), ". A general guide, not specific to this site."), m("ul", {
      className: "RMChain-products"
    }, chain.products.map(_ref1 => {
      let [name, use] = _ref1;
      return m("li", null, m("strong", null, name), m("span", {
        className: "RMMuted"
      }, use));
    }))) : null));
  }

  /** Suggestions waiting for review (admins and Data editors) */
  reviewPanel() {
    if (this.review === 'loading') return m("div", {
      className: "RMPanel is-empty"
    }, "Loading suggestions\u2026");
    return m("section", {
      className: "RMPanel",
      "aria-label": "Suggestions to review"
    }, m("div", {
      className: "RMPanel-head"
    }, m("h2", null, "Suggestions to review"), m("button", {
      type: "button",
      className: "Button",
      onclick: () => this.review = null
    }, "Close")), !this.review.length ? m("p", {
      className: "RMMuted"
    }, "Nothing waiting. Thank you!") : m("ul", {
      className: "RMList RMReview"
    }, this.review.map(x => m("li", null, m("div", {
      className: "RMReview-head"
    }, m("strong", null, x.name), m("span", {
      className: "RMMuted"
    }, x.kind === 'facility' ? (FACILITY_TYPES[x.type] || FACILITY_TYPES.other).label : x.kind === 'crop' ? 'Crop area' : 'Resource site', x.resource ? ' · ' + x.resource : '', " \xB7 ", [x.area, countryName(x.country)].filter(Boolean).join(', '), " \xB7 ", STATUSES[x.status] || x.status)), m("div", {
      className: "RMMuted RMSmall"
    }, "Suggested by ", x.by || 'a member', x.sent ? ' on ' + x.sent.slice(0, 10) : ''), x.note ? m("p", {
      className: "RMSmall"
    }, x.note) : null, (x.sources || []).length ? m("div", {
      className: "RMSmall"
    }, "Sources: ", x.sources.map(src => m("a", {
      href: src.url,
      target: "_blank",
      rel: "noopener"
    }, src.name || src.url, " "))) : m("div", {
      className: "RMSmall RMWarn"
    }, "No source given."), x.nearby && x.nearby.length ? m("div", {
      className: "RMSmall RMWarn"
    }, m("i", {
      className: "fas fa-exclamation-triangle"
    }), " Possible duplicate: ", x.nearby.map(n => n.name + ' (' + n.km + ' km)').join(', ')) : null, m("div", {
      className: "RMReview-actions"
    }, m("button", {
      type: "button",
      className: "Button",
      onclick: () => this.map && this.map.fit([x])
    }, m("i", {
      className: "fas fa-map-marker-alt"
    }), " Show on map"), m("button", {
      type: "button",
      className: "Button Button--primary",
      onclick: () => this.decide(x, true)
    }, m("i", {
      className: "fas fa-check"
    }), " Approve"), m("button", {
      type: "button",
      className: "Button RMDanger",
      onclick: () => this.decide(x, false)
    }, m("i", {
      className: "fas fa-times"
    }), " Reject"))))));
  }

  /** The add / edit form (admins and Data editors) */
  editor() {
    const e = this.edit;
    const d = e.data;
    const field = (label, input) => m("label", {
      className: "RMField"
    }, m("span", null, label), input);
    const text = function (key, placeholder) {
      if (placeholder === void 0) {
        placeholder = '';
      }
      return m("input", {
        type: "text",
        value: d[key] || '',
        placeholder: placeholder,
        oninput: ev => d[key] = ev.target.value
      });
    };
    const opts = (key, options, onchange) => m("select", {
      value: d[key] || '',
      onchange: ev => {
        d[key] = ev.target.value;
        if (onchange) onchange();
      }
    }, options.map(_ref10 => {
      let [v, t] = _ref10;
      return m("option", {
        value: v
      }, t);
    }));
    return m("section", {
      className: "RMPanel RMEditor",
      "aria-label": e.id ? 'Edit map item' : 'Add to map'
    }, m("div", {
      className: "RMPanel-head"
    }, m("h2", null, e.id ? 'Edit "' + (d.name || 'item') + '"' : e.mode === 'suggest' ? 'Suggest a resource or business' : 'Add to the map')), e.mode === 'suggest' ? m("p", {
      className: "RMMuted"
    }, "Suggest a resource site, crop area, factory or other facility. An editor checks every suggestion before it appears on the map. Please add a source link if you have one.") : null, m("div", {
      className: "RMFields"
    }, !e.id ? field('What is it?', opts('kind', e.mode === 'suggest' ? KINDS.filter(_ref11 => {
      let [k] = _ref11;
      return MEMBER_KINDS.includes(k);
    }) : KINDS, () => {
      d.lat = d.lng = null;
      d.geometry = [];
      this.pick();
    })) : null, field('Name', text('name', d.kind === 'rail' ? 'e.g. Lagos–Ibadan Railway' : d.kind === 'facility' ? 'e.g. Dangote Petroleum Refinery' : '')), d.kind === 'facility' ? field('Facility type', opts('type', Object.entries(FACILITY_TYPES).map(_ref12 => {
      let [k, t] = _ref12;
      return [k, t.label];
    }))) : null, d.kind === 'facility' ? field('What it produces / capacity', text('resource', 'e.g. Petrol and diesel · 650,000 barrels a day')) : null, d.kind === 'resource' ? field('Category', opts('category', CATEGORIES.filter(_ref13 => {
      let [k] = _ref13;
      return k !== 'agriculture';
    }))) : null, d.kind === 'resource' ? field('Resource', text('resource', 'e.g. Gold')) : null, d.kind === 'resource' ? field('Chemical element (if any)', opts('element', [['', 'None'], ...Object.entries(ELEMENTS).map(_ref14 => {
      let [s, n] = _ref14;
      return [s, s + ' · ' + n];
    })])) : null, d.kind === 'resource' && !d.element ? field('Short label on the map', text('symbol', 'e.g. Oil, Coal (max 8)')) : null, d.kind === 'crop' ? field('Crop', opts('crop', [['', 'Choose…'], ...CROPS.map(c => [c, c])])) : null, field('Country', opts('country', [['', 'Choose…'], ...COUNTRIES])), d.kind !== 'rail' ? field('Area (region, state)', text('area')) : null, field('Status', opts('status', Object.entries(STATUSES))), field('Came online / opened', m("input", {
      type: "date",
      value: d.online || '',
      oninput: ev => d.online = ev.target.value
    }))), field('Note', m("textarea", {
      rows: "2",
      maxlength: "600",
      value: d.note || '',
      oninput: ev => d.note = ev.target.value
    })), m("div", {
      className: "RMSources"
    }, m("span", null, "Sources"), d.sources.map((src, i) => m("div", {
      className: "RMSourceRow"
    }, m("input", {
      type: "text",
      placeholder: "Name (e.g. Ministry of Power)",
      value: src.name,
      oninput: ev => d.sources[i].name = ev.target.value
    }), m("input", {
      type: "url",
      placeholder: "https://\u2026",
      value: src.url,
      oninput: ev => d.sources[i].url = ev.target.value
    }))), d.sources.length < 5 ? m("button", {
      type: "button",
      className: "RMLinkish",
      onclick: () => d.sources.push({
        name: '',
        url: ''
      })
    }, "+ another source") : null), m("div", {
      className: "RMPlace"
    }, d.kind === 'rail' ? m("span", null, m("i", {
      className: "fas fa-route"
    }), " ", d.geometry.length, " points drawn", d.geometry.length > 1 ? ' · about ' + lineKm(d.geometry) + ' km' : '', ".", ' ', m("button", {
      type: "button",
      className: "RMLinkish",
      onclick: () => this.map && this.map.undoPoint()
    }, "Undo last point")) : m("span", null, m("i", {
      className: "fas fa-map-pin"
    }), " ", d.lat != null ? 'Placed at ' + d.lat.toFixed(3) + ', ' + d.lng.toFixed(3) : 'Not placed yet: click the map.')), e.error ? m("p", {
      className: "RMEditor-error",
      role: "alert"
    }, e.error) : null, m("div", {
      className: "RMEditor-actions"
    }, m("button", {
      type: "button",
      className: "Button Button--primary",
      disabled: e.saving,
      onclick: () => this.save()
    }, e.saving ? 'Saving…' : e.id ? 'Save changes' : e.mode === 'suggest' ? 'Send suggestion' : 'Add to map'), m("button", {
      type: "button",
      className: "Button",
      onclick: () => this.cancelEdit()
    }, "Cancel")));
  }
}

// Map plans (members' saved maps with routes and notes)
Object.assign(ResourceMapPage.prototype, planMethods);